import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import {
  Home, BookOpen, Activity, Flame, AlertTriangle, Apple, Moon, Cloud, Heart,
  Mic, Camera, FlaskConical, Syringe, Pill, CalendarDays, Clock, Headphones,
  Smile, ChefHat, Calendar, MessageSquare, Users, Bell, BookHeart, Settings,
  Bug, LogOut, Menu, X, MoonStar, Shield, GraduationCap, Image as ImageIcon, Newspaper
} from 'lucide-react';
import axios from 'axios';

const SidebarNavigation = ({ isOpen, onClose, showOnLanding = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token, API } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!token) {
        setIsAdmin(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/subscriptions/my-subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(response.data?.is_admin === true);
      } catch (error) {
        setIsAdmin(false);
      }
    };
    checkAdminStatus();
  }, [token, API]);

  const navItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    { icon: BookOpen, label: 'Knowledge', path: '/knowledge-base' },
    { icon: Activity, label: 'Symptom Tracker', path: '/tracker' },
    { icon: Flame, label: 'Flare Journal', path: '/flare-journal' },
    { icon: AlertTriangle, label: 'Flare Predictions', path: '/flare-predictions' },
    { icon: Apple, label: 'Food Tracker', path: '/food-tracker' },
    { icon: Moon, label: 'Sleep Tracker', path: '/sleep-tracker' },
    { icon: Cloud, label: 'Weather Tracker', path: '/weather-tracker' },
    { icon: Heart, label: 'Cycle Tracker', path: '/cycle-tracker' },
    { icon: Mic, label: 'Voice Journal', path: '/voice-journal' },
    { icon: Camera, label: 'Symptom Photos', path: '/symptom-photos' },
    { icon: FlaskConical, label: 'Lab Tracker', path: '/lab-tracker' },
    { icon: Syringe, label: 'Vaccine Tracker', path: '/vaccines' },
    { icon: Pill, label: 'Med & Supplement Tracker', path: '/supplements' },
    { icon: CalendarDays, label: 'Appointments', path: '/appointments' },
    { icon: Clock, label: 'Timeline', path: '/timeline' },
    { icon: ImageIcon, label: 'My Photos', path: '/my-photos' },
    { icon: Newspaper, label: 'What\'s New', path: '/news' },
    { icon: GraduationCap, label: 'Teacher Reports', path: '/manage-teachers' },
    { icon: Headphones, label: 'Parent Regulation', path: '/parent-regulation' },
    { icon: Smile, label: 'Child Regulation', path: '/child-regulation' },
    { icon: ChefHat, label: 'Recipes', path: '/recipes' },
    { icon: Calendar, label: 'Meal Planner', path: '/meal-planner' },
    { icon: MessageSquare, label: 'AI Chat', path: '/chat' },
    { icon: Users, label: 'Community', path: '/forum' },
    { icon: Bell, label: 'Reminders', path: '/reminders' },
    { icon: BookHeart, label: 'How to Use', path: '/how-to-use' },
    { icon: Bug, label: 'Report Issues', path: '/report-issue' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  // Admin nav item - only shown if user is admin
  const adminNavItem = { icon: Shield, label: 'Admin Panel', path: '/admin/promo-codes' };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNavClick = (path) => {
    if (showOnLanding && !user) {
      // If on landing page and not logged in, redirect to auth
      navigate('/auth', { state: { redirectTo: path } });
    } else {
      navigate(path);
    }
    onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    onClose();
  };

  const bgClass = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const textClass = isDarkMode ? 'text-[#e0e0e0]' : 'text-[#2D2D2D]';
  const hoverClass = isDarkMode ? 'hover:bg-[#2d3a4f]' : 'hover:bg-stone-100';
  const borderClass = isDarkMode ? 'border-[#2d3a4f]' : 'border-stone-200';

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-72 ${bgClass} z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } shadow-xl overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${borderClass}`}>
          <div className="flex items-center gap-2">
            <Heart className="w-7 h-7 text-primary fill-primary" strokeWidth={1.5} />
            <span className={`text-lg font-nunito font-bold text-primary`}>Calm Pathways</span>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${hoverClass} ${textClass}`}
            data-testid="close-sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        {user && (
          <div className={`px-4 py-3 border-b ${borderClass}`}>
            <p className={`text-sm font-medium ${textClass}`}>Hi, {user.name?.split(' ')[0] || 'User'}</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-stone-500'}`}>{user.email}</p>
          </div>
        )}

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  active
                    ? 'bg-primary/10 text-primary font-medium border-r-4 border-primary'
                    : `${textClass} ${hoverClass}`
                }`}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-primary' : ''}`} strokeWidth={1.5} />
                <span className="text-sm">{item.label}</span>
              </button>
            );
          })}
          
          {/* Admin Panel - Only show for admin users */}
          {isAdmin && (
            <button
              onClick={() => handleNavClick(adminNavItem.path)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                isActive(adminNavItem.path)
                  ? 'bg-primary/10 text-primary font-medium border-r-4 border-primary'
                  : `${textClass} ${hoverClass}`
              }`}
              data-testid="sidebar-admin-panel"
            >
              <Shield className={`w-5 h-5 ${isActive(adminNavItem.path) ? 'text-primary' : ''}`} strokeWidth={1.5} />
              <span className="text-sm">{adminNavItem.label}</span>
            </button>
          )}
        </div>

        {/* Footer Actions */}
        <div className={`border-t ${borderClass} p-3 space-y-2`}>
          {/* Sleep Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg ${hoverClass} ${textClass}`}
            data-testid="sidebar-sleep-mode"
          >
            <MoonStar className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-sm">Sleep Mode</span>
            <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-primary/20 text-primary' : 'bg-stone-200'}`}>
              {isDarkMode ? 'On' : 'Off'}
            </span>
          </button>

          {/* Logout */}
          {user && (
            <button
              onClick={handleLogout}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-red-500 hover:bg-red-50`}
              data-testid="sidebar-logout"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm">Logout</span>
            </button>
          )}

          {/* Login button for landing page */}
          {!user && showOnLanding && (
            <button
              onClick={() => { navigate('/auth'); onClose(); }}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white hover:bg-primary/90`}
              data-testid="sidebar-login"
            >
              <span className="text-sm font-medium">Login / Sign Up</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default SidebarNavigation;
